<template>
    <HomeSectionContainer title="Delete account">
        <template #subtitle>
            <p class="body-small text-valence-grey-750 mt-1">Permanently deactivate your account. You will no longer be able to log in or access any of your data.</p>
        </template>

        <button type="button" class="body-small text-red-600 underline hover:text-red-700" data-testid="delete-account-button" @click="showConfirmDialog = true">
            Delete account
        </button>

        <!-- Step 1: Consequences confirmation -->
        <ConfirmationDialog
            v-model:open="showConfirmDialog"
            title="Delete your account?"
            confirm-label="Continue"
            cancel-label="Cancel"
            container-class="md:max-w-2xl"
            @confirm="advanceToEmailConfirm"
            @cancel="showConfirmDialog = false">
            <template #content>
                <div class="flex flex-col gap-3">
                    <DeletionConsequences />
                </div>
            </template>
        </ConfirmationDialog>

        <!-- Step 2: Email confirmation -->
        <ConfirmationDialog
            v-model:open="showEmailConfirmDialog"
            title="Confirm account deletion"
            :confirm-label="isSubmitting ? 'Deleting…' : 'Delete account'"
            cancel-label="Cancel"
            confirm-theme="danger"
            container-class="md:max-w-2xl"
            :confirm-disabled="!canSubmit || isSubmitting"
            :close-on-confirm="false"
            @confirm="onFinalConfirm"
            @cancel="closeEmailConfirm">
            <template #content>
                <div class="flex flex-col gap-3 w-full">
                    <DeletionConsequences />
                    <label for="delete-account-note-input" class="body text-valence-grey-750 flex flex-col gap-1.5">
                        <span>Why are you deleting your account?</span>
                        <BaseTextarea
                            id="delete-account-note-input"
                            v-model:input="deletionNote"
                            rows="2"
                            :maxlength="2000"
                            placeholder="Tell us why you're deleting your account"
                            class="rounded-xl border border-valence-grey-300 px-3 py-2 body text-valence-grey-900 focus:border-valence-primary-500 focus:outline-none focus:ring-2 focus:ring-valence-primary-500/20"
                            data-testid="delete-note-input" />
                    </label>
                    <label for="delete-account-email-input" class="body text-valence-grey-750 flex flex-col gap-1.5">
                        <span
                            >Type your email address <strong>{{ userEmail }}</strong> to confirm.</span
                        >
                        <input
                            id="delete-account-email-input"
                            v-model="emailInput"
                            type="email"
                            class="w-full rounded-xl border border-valence-grey-300 px-3 py-2 body text-valence-grey-900 focus:border-valence-primary-500 focus:outline-none focus:ring-2 focus:ring-valence-primary-500/20"
                            placeholder="Enter your email address"
                            data-testid="delete-email-input"
                            @keyup.enter="onFinalConfirm" />
                    </label>
                    <p v-if="errorMessage" class="body-small text-red-600" data-testid="delete-error">{{ errorMessage }}</p>
                </div>
            </template>
        </ConfirmationDialog>
    </HomeSectionContainer>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { UserDetails } from "~vue/appTypes";
import HomeSectionContainer from "~vue/ChatHome/HomeSectionContainer.vue";
import ConfirmationDialog from "~vue/components/ConfirmationDialog.vue";
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import DeletionConsequences from "~vue/UserProfile/DeletionConsequences.vue";
import type { Ref } from "vue";
import { computed, inject, ref } from "vue";

const props = defineProps<{
    deleteAccountUrl: string;
}>();

const userDetails = inject<Ref<UserDetails>>("user_details");
const userEmail = computed(() => userDetails?.value?.email ?? "");

const showConfirmDialog = ref(false);
const showEmailConfirmDialog = ref(false);
const emailInput = ref("");
const deletionNote = ref("");
const errorMessage = ref("");
const isSubmitting = ref(false);

const emailMatches = computed(() => emailInput.value.toLowerCase().trim() === userEmail.value?.toLowerCase().trim());
const canSubmit = computed(() => emailMatches.value && deletionNote.value.trim().length > 0);

function advanceToEmailConfirm() {
    showConfirmDialog.value = false;
    showEmailConfirmDialog.value = true;
}

function closeEmailConfirm() {
    showEmailConfirmDialog.value = false;
    emailInput.value = "";
    deletionNote.value = "";
    errorMessage.value = "";
}

async function onFinalConfirm() {
    if (!canSubmit.value || isSubmitting.value) {
        return;
    }

    isSubmitting.value = true;
    errorMessage.value = "";

    const { data, error } = await useFetch(props.deleteAccountUrl, { immediate: true })
        .post({ reason: deletionNote.value.trim() })
        .json<{ redirect_url?: string; error?: string }>();

    if (error.value || !data.value?.redirect_url) {
        isSubmitting.value = false;
        errorMessage.value = (data.value as { error?: string } | null)?.error ?? "Something went wrong. Please try again.";
        return;
    }

    globalThis.location.href = data.value.redirect_url;
}
</script>
